// src/components/onboarding/PortfolioValueStep.jsx
// Required onboarding step — collects portfolio value before Pro modules unlock.
// NO skip option.

import { useState, useRef } from 'react';

const GREEN  = '#3ddc84';
const BG     = '#060a0f';
const SURFACE = '#0d1117';
const BORDER = '#1c2333';
const ERROR  = '#ff6b35';
const MUTED  = '#8b949e';

const CURRENCIES = [
  { code: 'EUR', symbol: '€' },
  { code: 'USD', symbol: '$' },
  { code: 'GBP', symbol: '£' },
];

export default function PortfolioValueStep({ onComplete, onCSVImport, userId }) {
  const [rawValue, setRawValue] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [focused, setFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const numericValue = parseFloat(rawValue.replace(/,/g, '')) || 0;
  const canContinue  = numericValue > 0 && !saving;
  const currSymbol   = CURRENCIES.find(c => c.code === currency)?.symbol ?? '$';

  function handleValueChange(e) {
    // Allow digits, one decimal point, commas
    const raw = e.target.value.replace(/[^0-9.,]/g, '');
    setRawValue(raw);
    setError(null);
  }

  async function handleContinue() {
    if (!canContinue) return;
    setSaving(true);
    setError(null);
    try {
      await onComplete(numericValue, currency);
    } catch (err) {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && canContinue) handleContinue();
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100%',
      padding: '40px 24px',
    }}>
      <style>{`
        @keyframes pv-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pv-card { animation: pv-fade-up .4s ease forwards; }
        .pv-input-wrap:focus-within .pv-currency-symbol {
          color: ${GREEN};
        }
      `}</style>

      <div className="pv-card" style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        width: 'min(480px, 100%)',
        padding: '40px 36px',
        boxShadow: '0 0 60px rgba(61,220,132,.06), 0 24px 48px rgba(0,0,0,.5)',
      }}>
        {/* Icon */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(61,220,132,.08)',
          border: `1px solid rgba(61,220,132,.2)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 24,
          fontFamily: 'var(--font-mono)', fontSize: 18, color: GREEN,
        }}>
          ◈
        </div>

        {/* Headline */}
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 18,
          fontWeight: 700,
          color: '#c9d1d9',
          letterSpacing: '.02em',
          marginBottom: 10,
          lineHeight: 1.3,
        }}>
          What is your current portfolio value?
        </div>

        {/* Subtext */}
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          color: MUTED,
          lineHeight: 1.6,
          marginBottom: 32,
        }}>
          This unlocks Backtest, Monte Carlo, Optimizer and your P&L tracking.
          You can update it any time from your Dashboard.
        </div>

        {/* Input row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          {/* Currency selector */}
          <select
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            style={{
              background: '#111111',
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              color: '#c9d1d9',
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              fontWeight: 700,
              padding: '0 10px',
              cursor: 'pointer',
              outline: 'none',
              flexShrink: 0,
              height: 52,
            }}
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>

          {/* Value input */}
          <div className="pv-input-wrap" style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            background: '#111111',
            border: `1px solid ${focused ? GREEN : BORDER}`,
            borderRadius: 6,
            padding: '0 14px',
            transition: 'border-color .15s',
            height: 52,
          }}>
            <span className="pv-currency-symbol" style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 20,
              fontWeight: 700,
              color: focused ? GREEN : MUTED,
              marginRight: 6,
              transition: 'color .15s',
              userSelect: 'none',
            }}>
              {currSymbol}
            </span>
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={rawValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoFocus
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 24,
                fontWeight: 700,
                color: '#c9d1d9',
                width: '100%',
              }}
            />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: ERROR,
            marginBottom: 12,
            letterSpacing: '.04em',
          }}>
            ⚠ {error}
          </div>
        )}

        {/* Continue button */}
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          style={{
            width: '100%',
            background: canContinue ? GREEN : 'rgba(61,220,132,.12)',
            border: 'none',
            color: canContinue ? '#0a0a0a' : 'rgba(61,220,132,.35)',
            padding: '14px 0',
            borderRadius: 6,
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: 700,
            cursor: canContinue ? 'pointer' : 'default',
            letterSpacing: '.1em',
            transition: 'background .15s, color .15s',
            boxShadow: canContinue ? '0 0 20px rgba(61,220,132,.25)' : 'none',
            marginBottom: 16,
          }}
          onMouseEnter={e => { if (canContinue) e.currentTarget.style.boxShadow = '0 0 32px rgba(61,220,132,.45)'; }}
          onMouseLeave={e => { if (canContinue) e.currentTarget.style.boxShadow = '0 0 20px rgba(61,220,132,.25)'; }}
        >
          {saving ? 'SAVING…' : 'CONTINUE →'}
        </button>

        {/* CSV import link */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onCSVImport}
            style={{
              background: 'transparent',
              border: 'none',
              color: MUTED,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              cursor: 'pointer',
              letterSpacing: '.06em',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(139,148,158,.4)',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = GREEN; }}
            onMouseLeave={e => { e.currentTarget.style.color = MUTED; }}
          >
            Import CSV instead
          </button>
        </div>
      </div>
    </div>
  );
}
